import { translateErrorCodeToMessage } from 'app/constants/error_messages'
import * as snugNotifier from 'app/services/snugNotifier'
import { api } from 'app/utils/api/helpers'

/**
 * Rental History API Service
 * Following the same patterns as onboardingApi.ts
 */

// TypeScript interfaces for rental history API
export interface RentalHistoryData {
  address: {
    friendlyName: string
    unitNumber: string
    streetNumber: string
    streetName: string
    suburb: string
    state: string
    postcode: string
    country: string
    lat?: number
    lng?: number
  }
  propertyArrangement: number | undefined
  startDate: string
  endDate: string
  rate?: string
  bond?: string
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  agencyName?: string
  reasonForLeaving?: string
  shouldContactReference?: boolean
  sendReferenceOnAdd?: boolean
  tenants?: Array<{
    firstName: string
    lastName: string
    email: string
    mobileNumber: string
  }>
  [key: string]: any
}

// Interface for manual rental history creation
export interface ManualRentalHistoryData {
  applicant_guid: string
  agency_guid: string
  applicant_details: {
    firstName: string
    lastName: string
    email: string
    mobileNumber: string
  }
  rental_history_request: {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    officeNumber?: string
    address: {
      unitNumber?: string
      streetNumber: string
      streetName: string
      suburb: string
      state: string
      postcode: string
      country: string
    }
    propertyType: number
    bedrooms?: string
    rate?: number
    bond?: number
    isRollingLease?: boolean
    startDate: string
    endDate?: string
    managerType: number
    agencyName?: string
    reasonForLeaving?: string
    isCurrentAddress?: boolean
    propertyArrangement: number
    personalMessage?: string
    isBondPaid?: boolean
    shouldContactReference?: boolean
    sendReferenceOnAdd?: boolean
    tenants?: Array<{
      firstName: string
      lastName: string
      email: string
      mobileNumber: string
    }>
    LedgersIDs?: any[]
    isQLDForm22?: boolean
  }
}

export interface ApiError {
  message?: string
  response?: {
    status: number
    data?: any
  }
}

export interface RentalHistoryResponse {
  id: string
  ledgerAnalysisJobId?: string
  [key: string]: any
}

/**
 * Create a new rental history entry
 */
export const createRentalHistory = async (
  rentalHistoryData: RentalHistoryData,
  agencyGUID: string = '',
): Promise<RentalHistoryResponse> => {
  try {
    let url = 'sm/reputation/rentalhistory'
    if (agencyGUID) {
      url += `?agency_id=${agencyGUID}`
    }

    const response = await api.post(url, rentalHistoryData)

    // Handle success notification
    snugNotifier.success('Rental history added successfully!')

    return response.data
  } catch (error) {
    console.error('Error creating rental history:', error)

    const apiError = error as ApiError
    // Handle specific error cases
    if (apiError.response?.status === 400) {
      throw new Error('Invalid rental history data. Please check your inputs.')
    } else if (apiError.response?.status === 409) {
      throw new Error('This rental history entry already exists.')
    } else if (apiError.response?.status && apiError.response.status >= 500) {
      throw new Error('Server error. Please try again later.')
    }

    throw new Error(apiError.message || 'Failed to create rental history')
  }
}

/**
 * Create a new manual rental history entry for applications
 */
export const createManualRentalHistory = async (
  rentalHistoryData: ManualRentalHistoryData,
): Promise<RentalHistoryResponse> => {
  try {
    const url = 'sm/reputation/rentalhistory/manual'
    const response = await api.post(url, rentalHistoryData)

    // Handle success notification
    snugNotifier.success('Manual rental history added successfully!')

    // Return the response directly since it's not wrapped in a data property
    return response
  } catch (error: any) {
    snugNotifier.error(translateErrorCodeToMessage(error.errorCode))

    const apiError = error as ApiError
    // Handle specific error cases
    if (apiError.response?.status === 400) {
      throw new Error('Invalid rental history data. Please check your inputs.')
    } else if (apiError.response?.status === 409) {
      throw new Error('This rental history entry already exists.')
    } else if (apiError.response?.status && apiError.response.status >= 500) {
      throw new Error('Server error. Please try again later.')
    }

    throw new Error(
      apiError.message || 'Failed to create manual rental history',
    )
  }
}

/**
 * Update an existing manual rental history entry
 */
export const updateManualRentalHistory = async (
  guidID: string,
  rentalHistoryData: ManualRentalHistoryData,
): Promise<RentalHistoryResponse> => {
  try {
    const url = `sm/reputation/rentalhistory/manual/${guidID}`
    const response = await api.put(url, rentalHistoryData)

    // Handle success notification
    snugNotifier.success('Manual rental history updated successfully!')

    // Return the response directly since it's not wrapped in a data property
    return response
  } catch (error: any) {
    snugNotifier.error(translateErrorCodeToMessage(error.errorCode))

    const apiError = error as ApiError
    // Handle specific error cases
    if (apiError.response?.status === 404) {
      throw new Error('Manual rental history not found.')
    } else if (apiError.response?.status === 400) {
      throw new Error('Invalid rental history data. Please check your inputs.')
    } else if (apiError.response?.status && apiError.response.status >= 500) {
      throw new Error('Server error. Please try again later.')
    }

    throw new Error(
      apiError.message || 'Failed to update manual rental history',
    )
  }
}

/**
 * Update an existing rental history entry
 */
export const updateRentalHistory = async (
  id: string,
  rentalHistoryData: RentalHistoryData,
  agencyGUID: string = '',
): Promise<RentalHistoryResponse> => {
  try {
    let url = `sm/reputation/rentalhistory/${id}`
    if (agencyGUID) {
      url += `?agency_id=${agencyGUID}`
    }

    const response = await api.put(url, rentalHistoryData)

    // Handle success notification
    snugNotifier.success('Rental history updated successfully!')

    return response.data
  } catch (error) {
    console.error('Error updating rental history:', error)

    const apiError = error as ApiError
    // Handle specific error cases
    if (apiError.response?.status === 404) {
      throw new Error('Rental history not found.')
    } else if (apiError.response?.status === 400) {
      throw new Error('Invalid rental history data. Please check your inputs.')
    } else if (apiError.response?.status && apiError.response.status >= 500) {
      throw new Error('Server error. Please try again later.')
    }

    throw new Error(apiError.message || 'Failed to update rental history')
  }
}

/**
 * Fetch all rental history entries for the current user
 */
export const fetchRentalHistoryList = async (): Promise<any[]> => {
  try {
    const response = await api.get('sm/reputation/rentalhistory')
    return response.data
  } catch (error) {
    console.error('Error fetching rental history list:', error)
    const apiError = error as ApiError
    throw new Error(apiError.message || 'Failed to fetch rental history')
  }
}

/**
 * Fetch a specific rental history entry by ID
 */
export const fetchRentalHistoryDetail = async (id: string): Promise<any> => {
  try {
    const response = await api.get(`sm/reputation/rentalhistory/${id}`)
    return response.data
  } catch (error) {
    console.error('Error fetching rental history detail:', error)

    const apiError = error as ApiError
    if (apiError.response?.status === 404) {
      throw new Error('Rental history not found.')
    }

    throw new Error(
      apiError.message || 'Failed to fetch rental history details',
    )
  }
}

/**
 * Delete a rental history entry
 */
export const deleteRentalHistory = async (
  id: string,
): Promise<{ success: boolean }> => {
  try {
    await api.delete(`sm/reputation/rentalhistory/${id}`)

    // Handle success notification
    snugNotifier.success('Rental history deleted successfully!')

    return { success: true }
  } catch (error) {
    console.error('Error deleting rental history:', error)

    const apiError = error as ApiError
    if (apiError.response?.status === 404) {
      throw new Error('Rental history not found.')
    }

    throw new Error(apiError.message || 'Failed to delete rental history')
  }
}

/**
 * Delete a manual rental history entry
 */
export const deleteManualRentalHistory = async (
  rentalHistoryId: string,
  applicantGuid: string,
  agencyGuid: string,
): Promise<{ success: boolean }> => {
  try {
    await api.delete(
      `sm/reputation/rentalhistory/manual/${rentalHistoryId}?applicant_guid=${applicantGuid}&agency_guid=${agencyGuid}`,
    )

    // Handle success notification
    snugNotifier.success('Manual rental history deleted successfully!')

    return { success: true }
  } catch (error: any) {
    snugNotifier.error(translateErrorCodeToMessage(error.errorCode))

    const apiError = error as ApiError
    if (apiError.response?.status === 404) {
      throw new Error('Manual rental history not found.')
    }

    throw new Error(
      apiError.message || 'Failed to delete manual rental history',
    )
  }
}

/**
 * Search for managers/agencies for rental history
 */
export const fetchRentalHistoryManagers = async (
  searchValue: string = '',
): Promise<any[]> => {
  try {
    const response = await api.get(
      `sm/reputation/managerrentalhistory?search=${searchValue}`,
    )
    return response
  } catch (error) {
    console.error('Error fetching rental history managers:', error)
    const apiError = error as ApiError
    throw new Error(apiError.message || 'Failed to fetch managers')
  }
}
